import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const ticketTypeSchema = z.object({
  name: z.string().min(1).max(100),
  price: z.number().min(0).max(100_000_000),
  total: z.number().int().min(1).max(100_000),
  description: z.string().max(500).optional(),
});

const eventFieldsSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  time: z.string().regex(/^\d{2}:\d{2}$/),
  location: z.string().min(1).max(200),
  city: z.string().min(1).max(100),
  category: z.string().min(1).max(50),
  imageUrl: z.string().url().max(2000).optional(),
  maxPerUser: z.number().int().min(1).max(20),
  isPublished: z.boolean(),
  ticketTypes: z.array(ticketTypeSchema).min(1).max(10),
  tenantId: z.string().uuid().optional(),
  // Opciones avanzadas del wizard (todas opcionales, con defaults en DB).
  endTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  timezone: z.string().max(64).optional(),
  isVirtual: z.boolean().optional(),
  virtualUrl: z.string().url().max(2000).optional(),
  capacity: z.number().int().min(1).max(1_000_000).optional(),
  waitingRoomEnabled: z.boolean().optional(),
  waitingRoomThreshold: z.number().int().min(10).max(1_000_000).optional(),
  salesStartAt: z.string().datetime().optional(),
  salesEndAt: z.string().datetime().optional(),
  refundPolicy: z.enum(["no_refund", "until_7_days", "until_48h", "flexible"]).optional(),
  collectAttendeeData: z.boolean().optional(),
  tags: z.array(z.string().max(30)).max(10).optional(),
});

export const createEvent = createServerFn({ method: "POST" })
  .inputValidator(eventFieldsSchema)
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { getAuthenticatedUserId } = await import("@/lib/auth.server");
    const userId = await getAuthenticatedUserId();

    // Check organizer role — if user has no role yet, assign organizer
    const { data: roles } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);

    const isOrganizer = roles?.some((r) => r.role === "organizer" || r.role === "admin");
    if (!isOrganizer) {
      await supabaseAdmin.from("user_roles").insert({ user_id: userId, role: "organizer" });
    }

    const { data: event, error: eventError } = await supabaseAdmin
      .from("events")
      .insert({
        title: data.title,
        description: data.description || null,
        date: data.date,
        time: data.time,
        location: data.location,
        city: data.city,
        category: data.category,
        image_url: data.imageUrl || null,
        max_per_user: data.maxPerUser,
        is_published: data.isPublished,
        organizer_id: userId,
        tenant_id: data.tenantId || null,
        end_time: data.endTime ?? null,
        timezone: data.timezone ?? "America/Costa_Rica",
        is_virtual: data.isVirtual ?? false,
        virtual_url: data.virtualUrl ?? null,
        capacity: data.capacity ?? null,
        // Sala de espera: encendida por defecto en todo evento nuevo.
        waiting_room_enabled: data.waitingRoomEnabled ?? true,
        waiting_room_threshold: data.waitingRoomThreshold ?? 500,
        sales_start_at: data.salesStartAt ?? null,
        sales_end_at: data.salesEndAt ?? null,
        refund_policy: data.refundPolicy ?? "no_refund",
        collect_attendee_data: data.collectAttendeeData ?? false,
        tags: data.tags ?? [],
      })
      .select("id")
      .single();

    if (eventError) throw new Error(`Error al crear evento: ${eventError.message}`);

    const ticketTypeInserts = data.ticketTypes.map((tt) => ({
      event_id: event.id,
      name: tt.name,
      price: tt.price,
      total: tt.total,
      available: tt.total,
      description: tt.description || null,
    }));

    const { error: ttError } = await supabaseAdmin
      .from("ticket_types")
      .insert(ticketTypeInserts);

    if (ttError) throw new Error(`Error al crear tipos de ticket: ${ttError.message}`);

    // Invalidar caché de listado público (el detalle aún no existe en cache).
    try {
      const { cachePurge } = await import("./cache.server");
      await cachePurge(["evt:list:v1:all"]);
    } catch {}

    return { success: true, eventId: event.id };
  });

async function isPlatformAdmin(userId: string): Promise<boolean> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  return !!data;
}

async function logAdminAction(adminId: string, action: string, eventId: string, notes?: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  await supabaseAdmin.from("admin_actions_log").insert({
    admin_id: adminId,
    action,
    event_id: eventId,
    target_type: "event",
    target_id: eventId,
    notes: notes || null,
  });
}

export const updateEvent = createServerFn({ method: "POST" })
  .inputValidator(z.object({ eventId: z.string().uuid() }).merge(eventFieldsSchema))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { getAuthenticatedUserId } = await import("@/lib/auth.server");
    const userId = await getAuthenticatedUserId();

    // Verify ownership OR admin
    const { data: existing } = await supabaseAdmin
      .from("events")
      .select("organizer_id")
      .eq("id", data.eventId)
      .single();

    if (!existing) throw new Error("Evento no encontrado");
    const isAdmin = await isPlatformAdmin(userId);
    const isOwner = existing.organizer_id === userId;
    if (!isOwner && !isAdmin) throw new Error("No tenés permisos para editar este evento");
    if (isAdmin && !isOwner) await logAdminAction(userId, "event.update", data.eventId);

    // Update event
    const { error: updateError } = await supabaseAdmin
      .from("events")
      .update({
        title: data.title,
        description: data.description || null,
        date: data.date,
        time: data.time,
        location: data.location,
        city: data.city,
        category: data.category,
        image_url: data.imageUrl || null,
        max_per_user: data.maxPerUser,
        is_published: data.isPublished,
      })
      .eq("id", data.eventId);

    if (updateError) throw new Error(`Error al actualizar: ${updateError.message}`);

    // Get existing ticket types
    const { data: existingTTs } = await supabaseAdmin
      .from("ticket_types")
      .select("id, total, available")
      .eq("event_id", data.eventId);

    // Delete old ticket types and insert new ones (adjust available based on sold)
    const oldTTs = existingTTs || [];

    // Remove old ticket types
    await supabaseAdmin
      .from("ticket_types")
      .delete()
      .eq("event_id", data.eventId);

    // Insert updated ticket types
    const ticketTypeInserts = data.ticketTypes.map((tt) => ({
      event_id: data.eventId,
      name: tt.name,
      price: tt.price,
      total: tt.total,
      available: tt.total, // reset available to total for new types
      description: tt.description || null,
    }));

    const { error: ttError } = await supabaseAdmin
      .from("ticket_types")
      .insert(ticketTypeInserts);

    if (ttError) throw new Error(`Error al actualizar tickets: ${ttError.message}`);

    try {
      const { cachePurge, eventCacheKeys } = await import("./cache.server");
      await cachePurge(eventCacheKeys(data.eventId));
    } catch {}

    return { success: true, eventId: data.eventId };
  });

export const getEventForEdit = createServerFn({ method: "POST" })
  .inputValidator(z.object({ eventId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { getAuthenticatedUserId } = await import("@/lib/auth.server");
    const userId = await getAuthenticatedUserId();

    const { data: event } = await supabaseAdmin
      .from("events")
      .select("*, ticket_types(*)")
      .eq("id", data.eventId)
      .single();

    if (!event) throw new Error("Evento no encontrado");
    const isAdmin = await isPlatformAdmin(userId);
    if (event.organizer_id !== userId && !isAdmin) {
      throw new Error("No tenés permisos para editar este evento");
    }

    return { event };
  });

export const cancelEvent = createServerFn({ method: "POST" })
  .inputValidator(z.object({ eventId: z.string().uuid(), reason: z.string().max(500).optional() }))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { getAuthenticatedUserId } = await import("@/lib/auth.server");
    const userId = await getAuthenticatedUserId();

    const { data: event } = await supabaseAdmin
      .from("events")
      .select("organizer_id, status")
      .eq("id", data.eventId)
      .single();
    if (!event) throw new Error("Evento no encontrado");
    const isAdmin = await isPlatformAdmin(userId);
    const isOwner = event.organizer_id === userId;
    if (!isOwner && !isAdmin) throw new Error("No tenés permisos para cancelar este evento");

    const { error } = await supabaseAdmin
      .from("events")
      .update({ status: "cancelled", is_published: false } as any)
      .eq("id", data.eventId);
    if (error) throw new Error(error.message);

    if (isAdmin && !isOwner) await logAdminAction(userId, "event.cancel", data.eventId, data.reason);

    try {
      const { cachePurge, eventCacheKeys } = await import("./cache.server");
      await cachePurge(eventCacheKeys(data.eventId));
    } catch {}

    return { success: true };
  });

export const deleteEvent = createServerFn({ method: "POST" })
  .inputValidator(z.object({ eventId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { getAuthenticatedUserId } = await import("@/lib/auth.server");
    const userId = await getAuthenticatedUserId();

    const { data: event } = await supabaseAdmin
      .from("events")
      .select("organizer_id, title")
      .eq("id", data.eventId)
      .single();

    if (!event) throw new Error("Evento no encontrado");
    const isAdmin = await isPlatformAdmin(userId);
    const isOwner = event.organizer_id === userId;
    if (!isOwner && !isAdmin) throw new Error("No tenés permisos para eliminar este evento");

    // Check for active/used/resale tickets (block hard delete for non-admins)
    const { count: activeCount } = await supabaseAdmin
      .from("tickets")
      .select("*", { count: "exact", head: true })
      .eq("event_id", data.eventId)
      .in("status", ["active", "used", "resale"]);

    if ((activeCount || 0) > 0 && !isAdmin) {
      throw new Error(`No se puede eliminar: hay ${activeCount} ticket(s) vendidos para este evento`);
    }

    // Detect any FK references that would block a hard delete (RESTRICT FKs)
    const [{ count: anyTickets }, { count: settlementsCount }, { count: resaleListingsCount }, { count: resaleTxCount }] = await Promise.all([
      supabaseAdmin.from("tickets").select("*", { count: "exact", head: true }).eq("event_id", data.eventId),
      supabaseAdmin.from("organizer_settlements").select("*", { count: "exact", head: true }).eq("event_id", data.eventId),
      supabaseAdmin.from("resale_listings").select("*", { count: "exact", head: true }).eq("event_id", data.eventId),
      supabaseAdmin.from("resale_transactions").select("*", { count: "exact", head: true }).eq("event_id", data.eventId),
    ]);

    const hasReferences = (anyTickets || 0) > 0 || (settlementsCount || 0) > 0 || (resaleListingsCount || 0) > 0 || (resaleTxCount || 0) > 0;

    if (hasReferences) {
      // Soft delete: preserve history, hide from listings
      const { error: softErr } = await supabaseAdmin
        .from("events")
        .update({ deleted_at: new Date().toISOString(), is_published: false, status: "cancelled" } as any)
        .eq("id", data.eventId);
      if (softErr) throw new Error(softErr.message);
      if (isAdmin && !isOwner) await logAdminAction(userId, "event.soft_delete", data.eventId, `Refs: tickets=${anyTickets} settlements=${settlementsCount} resale=${resaleListingsCount}`);
      try {
        const { cachePurge, eventCacheKeys } = await import("./cache.server");
        await cachePurge(eventCacheKeys(data.eventId));
      } catch {}
      return { success: true, softDeleted: true };
    }

    // Hard delete (no references). ticket_types/holds/zone_settings/staff cascade automatically.
    const { error: delErr } = await supabaseAdmin.from("events").delete().eq("id", data.eventId);
    if (delErr) throw new Error(delErr.message);

    if (isAdmin && !isOwner) await logAdminAction(userId, "event.hard_delete", data.eventId, `Título: ${event.title}`);

    try {
      const { cachePurge, eventCacheKeys } = await import("./cache.server");
      await cachePurge(eventCacheKeys(data.eventId));
    } catch {}

    return { success: true };
  });
